import { MAIN_CHAT_MODE, useSDK, useStores } from '@littlebird/sdk';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { backendApis } from '@/backendApis';
import { getPostAuthLandingRoute } from '@/features/welcome';
import { userStore } from '@/stores/user.store';
import { Routes } from '@/types/routes';
import { trackEvent } from '@/utils/analytics';

import { markWebOnboardingCompletedThisSession } from './web-onboarding.flags';
import {
  type PendingIntegrationThread,
  webOnboardingThreadsStore,
} from './web-onboarding.handoff';
import {
  openOAuthPopup,
  startIntegrationConnect,
} from './web-onboarding.oauth';

export type WebOnboardingChoice = {
  id: 'email' | 'calendar' | 'chat';
  label: string;
  integration?: { toolkitName: string; displayName: string };
  /** Runs as the thread's first chat message once the user lands in the app. */
  firstMessage?: string;
};

const WEB_ONBOARDING_CHOICES: WebOnboardingChoice[] = [
  {
    id: 'email',
    label: 'Organize my email',
    integration: { toolkitName: 'gmail', displayName: 'Gmail' },
    firstMessage:
      'Take a look at my email and tell me what needs my attention.',
  },
  {
    id: 'calendar',
    label: 'See my calendar',
    integration: {
      toolkitName: 'googlecalendar',
      displayName: 'Google Calendar',
    },
    firstMessage: "What's coming up on my calendar?",
  },
  {
    id: 'chat',
    label: 'Just chat for now',
  },
];

export type WebOnboardingPhase = 'choosing' | 'finishing' | 'error';

const fetchConnectedToolkits = async (): Promise<Set<string>> => {
  try {
    const accounts = await backendApis.integrations.getConnectedAccounts();
    return new Set(
      accounts
        .filter((account) => account.connection_status === 'connected')
        .map((account) => account.toolkit_name)
    );
  } catch {
    return new Set();
  }
};

/** Long enough for the card's fade-out to play before the route swaps. */
const EXIT_FADE_MS = 400;
const THREAD_SYNC_TIMEOUT_MS = 30_000;
const THREAD_SYNC_POLL_MS = 50;

/** The sidebar only lists synced threads, so the coach mark waits for sync. */
async function waitForThreadSynced(
  threadsStore: ReturnType<typeof useStores>['threads'],
  threadId: string
): Promise<void> {
  const deadline = Date.now() + THREAD_SYNC_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (threadsStore.getById(threadId)?.synced) return;
    await new Promise((resolve) => setTimeout(resolve, THREAD_SYNC_POLL_MS));
  }
  throw new Error(`Timed out waiting for thread ${threadId} to sync`);
}

async function createAndSyncWebOnboardingIntegrationThreads(
  draftsStore: ReturnType<typeof useStores>['drafts'],
  threadsStore: ReturnType<typeof useStores>['threads'],
  entries: PendingIntegrationThread[]
): Promise<string[]> {
  // Bootstrap hydrates threads eagerly, so this is normally a no-op. It stays
  // because a later loadInitialPage() clear()s MobX and would drop threads that
  // are not yet in the local database (handoff creates them before
  // syncThreadToServer persists).
  await threadsStore.ensureListLoadedFromStorage();

  const threadIds: string[] = [];
  for (const entry of entries) {
    const draftId = `web-onboarding-${entry.toolkitName}`;
    draftsStore.createDraft({ draftId, content: entry.firstMessage });
    const thread = threadsStore.initializeThreadWithMessage({
      mode: MAIN_CHAT_MODE,
      useTodos: false,
      draftId,
      title: entry.title,
    });
    await waitForThreadSynced(threadsStore, thread.id);
    threadIds.push(thread.id);
  }
  return threadIds;
}

export const useCompleteWebOnboarding = () => {
  const navigate = useNavigate();

  return useCallback(
    async (
      pendingThreads: PendingIntegrationThread[],
      landingRoute?: Routes
    ) => {
      if (pendingThreads.length > 0) {
        webOnboardingThreadsStore.setPendingThreads(pendingThreads);
      }

      const exitFade = new Promise((resolve) =>
        setTimeout(resolve, EXIT_FADE_MS)
      );
      await Promise.all([
        userStore.syncUpdate(
          { is_onboarded_web: true },
          { throwOnError: true }
        ),
        exitFade,
      ]);

      markWebOnboardingCompletedThisSession();
      // Pending integration threads are created after landing on /chats.
      const route =
        landingRoute ??
        (pendingThreads.length > 0 ? Routes.Chats : getPostAuthLandingRoute());
      await navigate({ to: route as string, replace: true });
    },
    [navigate]
  );
};

export const useWebOnboardingFlow = () => {
  const sdk = useSDK();
  const [phase, setPhase] = useState<WebOnboardingPhase>('choosing');
  const [addedIntegrations, setAddedIntegrations] = useState<
    WebOnboardingChoice[]
  >([]);

  useEffect(() => {
    trackEvent('WEB_ONBOARDING_STARTED');
    // Warm the SDK while the user reads the greeting, so the handoff into the
    // app doesn't need a loading splash.
    void sdk.initialize().catch(() => {});
  }, [sdk]);

  const completeOnboarding = useCompleteWebOnboarding();

  const finish = useCallback(async () => {
    setPhase('finishing');
    try {
      trackEvent('WEB_ONBOARDING_COMPLETED', {
        integrations: addedIntegrations.map((choice) => choice.id),
      });

      // A thread should exist for every integration that is set up, not just
      // the ones clicked this session — a user may have connected some during
      // an earlier onboarding attempt.
      const connectedToolkits = await fetchConnectedToolkits();
      const threadChoices = WEB_ONBOARDING_CHOICES.filter(
        (choice) =>
          choice.integration &&
          choice.firstMessage &&
          (addedIntegrations.some((added) => added.id === choice.id) ||
            connectedToolkits.has(choice.integration.toolkitName))
      );
      const pendingEntries = threadChoices.map((choice) => ({
        toolkitName: choice.integration!.toolkitName,
        title: choice.label,
        firstMessage: choice.firstMessage!,
      }));
      await completeOnboarding(pendingEntries);
    } catch (error) {
      console.error('Web onboarding completion failed:', error);
      setPhase('error');
    }
  }, [addedIntegrations, completeOnboarding]);

  const selectChoice = useCallback(
    (choice: WebOnboardingChoice) => {
      if (phase === 'finishing') return;

      trackEvent('WEB_ONBOARDING_CHOICE_CLICKED', {
        choice: choice.id,
        label: choice.label,
      });

      if (!choice.integration) {
        void finish();
        return;
      }

      if (addedIntegrations.some((added) => added.id === choice.id)) return;
      const popup = openOAuthPopup('about:blank');
      setAddedIntegrations((previous) => [...previous, choice]);
      setPhase('choosing');
      void startIntegrationConnect(
        choice.integration.toolkitName,
        popup,
        'web_onboarding'
      );
    },
    [phase, addedIntegrations, finish]
  );

  const remainingChoices = WEB_ONBOARDING_CHOICES.filter(
    (choice) => !addedIntegrations.some((added) => added.id === choice.id)
  );

  return { phase, addedIntegrations, remainingChoices, selectChoice };
};

/**
 * Runs once in the authenticated app shell (after the SDK is ready): creates
 * one synced thread per picked integration, arms the sidebar coach mark, and
 * opens the first thread.
 */
export const useCreateWebOnboardingThreads = (enabled = true) => {
  const navigate = useNavigate();
  const { drafts: draftsStore, threads: threadsStore } = useStores();

  useEffect(() => {
    if (!enabled) return;
    const pending = webOnboardingThreadsStore.consumePendingThreads();
    if (pending.length === 0) return;

    void (async () => {
      try {
        const threadIds = await createAndSyncWebOnboardingIntegrationThreads(
          draftsStore,
          threadsStore,
          pending
        );
        webOnboardingThreadsStore.setThreads(threadIds);
        const firstThreadId = threadIds[0];
        if (firstThreadId) {
          await navigate({
            to: '/chats/$chatId',
            params: { chatId: firstThreadId },
            replace: true,
          });
        }
      } catch (error) {
        console.error('Web onboarding thread handoff failed:', error);
      }
    })();
  }, [draftsStore, enabled, navigate, threadsStore]);
};
